import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import { Link } from 'react-router-dom';

interface PageBannerProps {
    title?: string;
    highlightedText?: string;
    description?: string;
    className?: string;
    backgroundImage?: string;
    titleFontSize?: string | number;
    highlightedTextFontSize?: string | number;
    overlay?: {
        color?: string;
        opacity?: number;
    };
}

const BannerContainer = styled(Box, {
    shouldForwardProp: (prop) => !['backgroundImage', 'overlay'].includes(prop as string),
})<{ backgroundImage?: string; overlay?: { color?: string; opacity?: number } }>(
    ({ backgroundImage, overlay }) => ({
        height: '95%',
        padding: '20px',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        borderRadius: '15px',
        margin: '10px',
        marginTop: '15px',
        '&::before': overlay ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: overlay.color || 'rgba(0, 0, 0, 0.5)',
            opacity: overlay.opacity ?? 0.5,
            borderRadius: '15px',
        } : {},
    })
);

const BannerTitle = styled(Typography)(({ }) => ({
    fontSize: '20px',
    fontWeight: 700,
    color: '#fff',
    textTransform: 'uppercase',
    position: 'relative',
}));

const BannerDescription = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.8)',
    maxWidth: '90%',
    position: 'relative',
    fontWeight: 400,
    [theme.breakpoints.up('md')]: {
        maxWidth: '80%',
    },
}));

const PageBanner: React.FC<PageBannerProps> = ({
    title,
    highlightedText,
    description,
    className,
    backgroundImage,
    titleFontSize = '30px',
    highlightedTextFontSize = '45px',
    overlay,
}) => {
    return (
        <BannerContainer
            className={`singlePageBanner ${className || ''}`}
            backgroundImage={backgroundImage}
            overlay={overlay}
        >
            <Box sx={{ padding: '20px', marginTop: '0px', position: 'relative' }}>
                <BannerTitle
                    variant="h1"
                    sx={{
                        fontSize: {
                            xs: '18px',
                            sm: titleFontSize,
                        },
                    }}
                >
                    {title} {highlightedText && (
                        <Typography
                            sx={{
                                fontSize: {
                                    xs: '28px',
                                    sm: highlightedTextFontSize,
                                },
                                fontWeight: '700',
                                display: 'block',
                                color: '#F5E003',
                                lineHeight: typeof highlightedTextFontSize === 'string'
                                    ? highlightedTextFontSize
                                    : `${highlightedTextFontSize}px`
                            }}
                        >
                            {highlightedText}
                        </Typography>
                    )}
                </BannerTitle>
                {description && <BannerDescription variant="body1">{description}</BannerDescription>}
            </Box>
            <Link to="/en/privacy-policy" className='innerPolicyLink'>Privacy Policy</Link>
        </BannerContainer>
    );
};

export default PageBanner;